import { useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { Select } from 'antd';

//* HOC's
import { withDataContext, withLanguageContext } from 'context';

//* Helpers
import { routerToPathAndQuery } from 'helpers/_routerToPathAndQuery';
import { getParamsByKey } from 'helpers/_getParamsByKey';
import { usePreserveScroll } from 'helpers';

//* Components
import Container from 'components/common/Container';
import Page from 'components/common/Page';
import Icon from 'components/common/Icon';
import Text from 'components/common/Text';
import FilterBar from 'components/pages/events/FilterBar';
import EventList from 'components/pages/events/EventList';
import Empty from 'components/common/Empty';

//* Styles
import EventStyle from 'styles/pages/EventsStyle';

const Events = ({ pages, pageParams, selectedLang, translate, dataProviderState }) => {
	//! Refs
	const selectWrapRef = useRef();

	//! Page Data
	const pageData = useMemo(() => pages?.[pageParams.name]?.[selectedLang], [pages, selectedLang, pageParams.name]);
	const eventsData = useMemo(() => dataProviderState?.events?.[pageParams?.queryName]?.[selectedLang], [dataProviderState, selectedLang, pageParams.queryName]);

	//! Router
	const router = useRouter();

	//! Query Param Keys
	const queryParamKeys = useMemo(() => pageParams.postType.queryParamKeys, [pageParams]);

	//! Region Value
	const regionValue = useMemo(() => {
		const regionParam = queryParamKeys[0]; //? "regionId"
		const activeItems = getParamsByKey(router, regionParam);

		return Number(activeItems?.[0]) || null;
	}, [router, queryParamKeys]);

	//! On Category Change
	const onCategoryChange = useCallback(
		(type, typeId, multiple = false) => {
			const { pathname, query } = routerToPathAndQuery(router, type, typeId, multiple);

			router.push({ pathname, query }, undefined, { scroll: false });
		},
		[router.query]
	);

	//! Store select options
	const storeRegionOptions = useMemo(() => {
		return pageData?.regions?.map((region) => (
			<Select.Option
				key={region.id}
				value={region.id}
			>
				{region.name}
			</Select.Option>
		));
	}, [pageData?.region]);

	usePreserveScroll();

	return (
		<Page
			meta={pageData?.content?.meta}
			nextSeoProps={{
				noindex: selectedLang != 'en',
			}}
			hrefLang={false}
			transparentHeader={pageParams.transparentHeader && !pageData}
		>
			{pageData && (
				<EventStyle>
					<Container
						section
						className='main-container'
					>
						<div className='page-top'>
							<Text
								tag={'h3'}
								className={'select-outer-text font-ping-bolder'}
								text={pageData.content.title}
							/>
							<div
								className='select-wrap'
								ref={selectWrapRef}
							>
								<Select
									dropdownAlign={{ offset: [0, 20] }}
									getPopupContainer={() => selectWrapRef.current}
									onChange={(e) => onCategoryChange('regionId', e)}
									value={regionValue}
									className='page-top-select'
									suffixIcon={
										<Icon
											name={'arrow'}
											className={'select-arrow'}
										/>
									}
								>
									<Select.Option value={null}>{translate('allPlaces')}</Select.Option>
									{storeRegionOptions}
								</Select>
							</div>
						</div>
						<FilterBar
							queryParamKeys={queryParamKeys}
							categories={pageData.categories}
							tags={pageData.tags}
							onCategoryChange={onCategoryChange}
						/>
						{eventsData?.events.length !== 0 ? (
							<EventList
								router={router}
								eventsData={eventsData}
								pageParams={pageParams}
								onCategoryChange={onCategoryChange}
							/>
						) : (
							<Empty hasSubtitle={false} />
						)}
					</Container>
				</EventStyle>
			)}
		</Page>
	);
};

export default withLanguageContext(withDataContext(Events, ['pages', 'dataProviderState']), ['translate']);
